import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import FedExIcon from "@/assets/icons/fedex.svg?react";
import {
  ClientDeliveryStatusEnum,
  DeliveryStatusEnum,
} from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { ClientRole, RolesEnum } from "@/constants/enums/roles.enum";
import { EXPEDITION_BLOCKED_REASON_COLORS } from "@/constants/options/expeditionBlockedReason.option";
import {
  useCompanyStatusLabels,
  useCustomQuotationTypeLabels,
  useDeliveryStatusLabels,
  useExpeditionBlockedReasonLabels,
  useExpeditionTypeLabels,
  useFedexPackagingLabels,
  useIdentityDocumentRequiredLabels,
  useImportLicenseRequiredLabels,
  useInvoicePaymentStatusLabels,
  useManualTrackingLabels,
  useOtherCustomQuotationTypeLongLabels,
  usePackagingFilterLabels,
  useParcelProductTypeLabels,
  usePaymentMethodLabels,
  usePaymentMethodShortLabels,
  usePickupTimeCotationLabels,
  usePickupTimeLabels,
  useRegionCustomLabels,
  useRoleLabels,
  useWineColorLabels,
} from "@/hooks/useEnumLabels";
import { optionsFromLabels } from "@/lib/optionsFromLabels.util";
import { DaysOption } from "@/types/bo/pickupDays";
import { FinalizeApiOption } from "@/types/finalizeApi.type";
import { DisplayType } from "@/types/invoiceDisplayTypes";
import { INVOICE_PAYMENT_STATUS_COLORS } from "@/types/invoiceStatus";
import { PICKUP_DAYS } from "@/types/pickupDay";

/**
 * Options `{ value, label }` des sélecteurs basés sur des enums (libellés du namespace `enums`).
 * L'ordre des options est celui des hooks de `useEnumLabels.ts`.
 */

export const useRoleOptions = () => {
  const labels = useRoleLabels();
  return useMemo(() => optionsFromLabels<RolesEnum>(labels), [labels]);
};

const CLIENT_ROLES: readonly ClientRole[] = [
  RolesEnum.SUPERADMIN,
  RolesEnum.ADMIN,
  RolesEnum.MEMBER,
  RolesEnum.ACCOUNTANT,
];

export const useClientRoleOptions = (): { value: ClientRole; label: string }[] => {
  const labels = useRoleLabels();
  return useMemo(() => CLIENT_ROLES.map((value) => ({ value, label: labels[value] })), [labels]);
};

export const useCompanyStatusOptions = () => {
  const labels = useCompanyStatusLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useCustomQuotationTypeOptions = () => {
  const labels = useCustomQuotationTypeLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useDeliveryStatusOptions = () => {
  const labels = useDeliveryStatusLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useClientDeliveryStatusOptions = () => {
  const labels = useDeliveryStatusLabels();
  return useMemo(
    () => [
      { value: ClientDeliveryStatusEnum.WAITING, label: labels[DeliveryStatusEnum.WAITING] },
      { value: ClientDeliveryStatusEnum.ONGOING, label: labels[DeliveryStatusEnum.ONGOING] },
      { value: ClientDeliveryStatusEnum.DELIVERED, label: labels[DeliveryStatusEnum.DELIVERED] },
    ],
    [labels],
  );
};

/** Motifs de blocage sélectionnables (sans AUTO), avec leur couleur. */
export const useExpeditionBlockedReasonOptions = () => {
  const labels = useExpeditionBlockedReasonLabels();
  return useMemo(
    () =>
      optionsFromLabels(labels)
        .filter((option) => option.value !== ExpeditionBlockedReasonEnum.AUTO)
        .map((option) => ({ ...option, color: EXPEDITION_BLOCKED_REASON_COLORS[option.value] })),
    [labels],
  );
};

export const useExpeditionTypeOptions = () => {
  const labels = useExpeditionTypeLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useFedexPackagingOptions = () => {
  const labels = useFedexPackagingLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useIdentityDocumentRequiredOptions = () => {
  const labels = useIdentityDocumentRequiredLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useImportLicenseRequiredOptions = () => {
  const labels = useImportLicenseRequiredLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useManualTrackingOptions = () => {
  const labels = useManualTrackingLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

/** Types "Autre" d'une demande spécifique, libellés longs. */
export const useOtherCustomQuotationTypeOptions = () => {
  const labels = useOtherCustomQuotationTypeLongLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const usePackagingFilterOptions = () => {
  const labels = usePackagingFilterLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useParcelProductTypeOptions = () => {
  const labels = useParcelProductTypeLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const usePaymentMethodOptions = () => {
  const labels = usePaymentMethodLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const usePaymentMethodShortOptions = () => {
  const labels = usePaymentMethodShortLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

/** Créneaux d'enlèvement (matin / après-midi). */
export const usePickupSlotOptions = () => {
  const labels = usePickupTimeCotationLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

/** Créneaux d'une zone de collecte (matin / après-midi / toute la journée). */
export const usePickupTimeOptions = () => {
  const labels = usePickupTimeLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useRegionCustomOptions = () => {
  const labels = useRegionCustomLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

export const useWineColorOptions = () => {
  const labels = useWineColorLabels();
  return useMemo(() => optionsFromLabels(labels), [labels]);
};

/** Statuts de paiement d'une facture, avec leurs couleurs de badge. */
export const useInvoicePaymentStatusOptions = () => {
  const labels = useInvoicePaymentStatusLabels();
  return useMemo(
    () =>
      optionsFromLabels(labels).map((option) => ({
        ...option,
        ...INVOICE_PAYMENT_STATUS_COLORS[option.value],
      })),
    [labels],
  );
};

/** Filtre de statut de paiement : "Tous" (valeur vide) puis Payé, Impayé, En traitement. */
export const useInvoicePaymentStatusFilterOptions = (): { value: string; label: string }[] => {
  const { t } = useTranslation("enums");
  const labels = useInvoicePaymentStatusLabels();
  return useMemo(
    () => [
      { value: "", label: t("invoicePaymentStatusFilter.all") },
      { value: "PAID", label: labels.PAID },
      { value: "TOBEPAID", label: labels.TOBEPAID },
      { value: "PROCESSING", label: labels.PROCESSING },
    ],
    [t, labels],
  );
};

const INVOICE_DISPLAY_TYPES = [
  "invoiceToBeValidated",
  "invoice",
  "invoiceBlocked",
  "litige",
] as const satisfies DisplayType[];

/** Onglets de la page factures du back-office. */
export const useInvoiceDisplayTypeOptions = () => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      INVOICE_DISPLAY_TYPES.map((value) => ({ value, label: t(`invoiceDisplayType.${value}`) })),
    [t],
  );
};

/**
 * Modes de finalisation d'une expédition.
 * NONE_SIMPLIFIED n'est disponible que pour les demandes spécifiques : il permet de finaliser
 * une demande spécifique avec un processus simplifié, quand elle ne rentre pas dans le
 * processus de cotation standard.
 */
export const useFinalizeApiOptions = (): FinalizeApiOption[] => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => [
      {
        value: "FEDEX",
        label: t("finalizeApi.FEDEX"),
        Icon: FedExIcon,
        headerSelect: {
          optionLabel: t("finalizeApiShort.FEDEX"),
          triggerLabel: t("finalizeApiShort.FEDEX"),
        },
      },
      {
        value: "NONE",
        label: t("finalizeApi.NONE"),
        headerSelect: {
          optionLabel: t("finalizeApiShort.NONE"),
          triggerLabel: t("finalizeApiShort.NONE"),
        },
      },
      {
        value: "NONE_SIMPLIFIED",
        label: t("finalizeApi.NONE_SIMPLIFIED"),
        headerSelect: {
          optionLabel: t("finalizeApiShort.NONE_SIMPLIFIED"),
          triggerLabel: t("finalizeApiShort.NONE_SIMPLIFIED"),
        },
      },
    ],
    [t],
  );
};

/** Jours d'enlèvement : initiale (`label`), abréviation (`shorten`) et nom complet (`long`). */
export const usePickupDayOptions = (): DaysOption[] => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      PICKUP_DAYS.map((value) => ({
        value,
        label: t(`pickupDayInitial.${value}`),
        shorten: t(`pickupDayShort.${value}`),
        long: t(`pickupDay.${value}`),
      })),
    [t],
  );
};
